import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Calendar, User, AlertCircle, Image as ImageIcon, Trash2, Download, ExternalLink, ClipboardList, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useProductFeedback } from '@/hooks/useProductFeedback';
import { useAppUsers } from '@/hooks/useAppUsers';
import { useCreateBacklogItem } from '@/hooks/useItBacklog';
import { formatDateTime, formatDateTimeShort } from "@/lib/dateUtils";
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { STATUS_CONFIG as BACKLOG_STATUS_CONFIG } from '@/types/itBacklog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_CONFIG = {
  open: { label: 'Open', className: 'bg-orange-500/10 text-orange-500 border-orange-500/20' },
  in_progress: { label: 'In Progress', className: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  resolved: { label: 'Resolved', className: 'bg-green-500/10 text-green-500 border-green-500/20' },
  closed: { label: 'Closed', className: 'bg-gray-500/10 text-gray-500 border-gray-500/20' },
};

const PRIORITY_CONFIG = {
  low: { label: '🟢 Low', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  medium: { label: '🟡 Medium', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  high: { label: '🟠 High', className: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
  critical: { label: '🔴 Critical', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
};

const TYPE_CONFIG = {
  bug: { 
    label: 'Bug', 
    icon: '🐛',
    className: 'bg-red-500/10 text-red-600 border-red-500/20' 
  },
  feature: { 
    label: 'Feature', 
    icon: '✨',
    className: 'bg-purple-500/10 text-purple-600 border-purple-500/20' 
  },
  improvement: { 
    label: 'Improvement', 
    icon: '📈',
    className: 'bg-blue-500/10 text-blue-600 border-blue-500/20' 
  },
};

const APPROVAL_CONFIG = {
  pending_approval: { label: 'Pending Approval', className: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  approved: { label: 'Approved', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  rejected: { label: 'Rejected', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
};

export default function FeedbackDetail({ feedback, open, onClose, isAdmin }) {
  const { t } = useTranslation();
  const { getScreenshotUrl, updateFeedback, deleteFeedback, isUpdating, isDeleting } = useProductFeedback();
  const { users } = useAppUsers();
  const { mutateAsync: createBacklogItem, isPending: isCreatingBacklog } = useCreateBacklogItem();
  const [editMode, setEditMode] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [formData, setFormData] = useState({
    type: feedback.type,
    status: feedback.status,
    priority: feedback.priority,
    approval_status: feedback.approval_status || '',
    assigned_to_user_id: feedback.assigned_to_user_id || '',
    admin_notes: feedback.admin_notes || '',
  });
  const [screenshotExpanded, setScreenshotExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [loadingScreenshot, setLoadingScreenshot] = useState(true);

  // Check if backlog item already exists for this feedback
  const { data: linkedBacklog, refetch: refetchLinkedBacklog } = useQuery({
    queryKey: ['backlog-for-feedback', feedback.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('it_backlog')
        .select('id, title, status')
        .eq('related_bug_report_id', feedback.id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!feedback.id,
  });

  useEffect(() => {
    setFormData({
      type: feedback.type,
      status: feedback.status,
      priority: feedback.priority,
      approval_status: feedback.approval_status || '',
      assigned_to_user_id: feedback.assigned_to_user_id || '',
      admin_notes: feedback.admin_notes || '',
    });
    setImageError(false);
  }, [feedback]);

  // Load signed screenshot URL
  useEffect(() => {
    const loadScreenshotUrl = async () => {
      if (!feedback.screenshot_path) {
        setScreenshotUrl(null);
        setLoadingScreenshot(false);
        return;
      }

      setLoadingScreenshot(true);
      try {
        const url = await getScreenshotUrl(feedback.screenshot_path);
        setScreenshotUrl(url);
      } catch (error) {
        console.error('Error loading screenshot:', error);
        setImageError(true);
      } finally {
        setLoadingScreenshot(false);
      }
    };

    loadScreenshotUrl();
  }, [feedback.screenshot_path, getScreenshotUrl]);

  const handleSave = () => {
    updateFeedback({
      id: feedback.id,
      type: formData.type,
      status: formData.status,
      priority: formData.priority,
      approval_status: formData.approval_status && formData.approval_status !== '' ? formData.approval_status : null,
      assigned_to_user_id: formData.assigned_to_user_id && formData.assigned_to_user_id !== 'unassigned' ? formData.assigned_to_user_id : null,
      admin_notes: formData.admin_notes || null,
    });
    setEditMode(false);
  };

  const handleApprove = async () => {
    console.log('=== handleApprove iniciado ===');
    console.log('Feedback ID:', feedback.id);
    console.log('Feedback Title:', feedback.title);
    console.log('Feedback Type:', feedback.type);
    console.log('linkedBacklog existente:', linkedBacklog);

    // Check if backlog item already exists
    if (linkedBacklog) {
      console.log('Backlog já existe, apenas atualizando status');
      toast.info(t('productFeedback.detail.backlogExists'));
      updateFeedback({
        id: feedback.id,
        approval_status: 'approved',
      });
      return;
    }

    // Map feedback type to backlog category
    const categoryMap = {
      bug: 'bug_fix',
      feature: 'feature',
      improvement: 'improvement',
    };

    const backlogData = {
      title: feedback.title,
      description: feedback.description,
      category: categoryMap[feedback.type] || 'feature',
      module: feedback.module,
      priority: feedback.priority || 'medium',
      related_bug_report_id: feedback.id,
      requested_by: feedback.reported_by_user_id,
      notes: `Criado automaticamente a partir do feedback aprovado`,
    };

    console.log('Dados para criar backlog item:', backlogData);

    try {
      // Create backlog item FIRST
      console.log('Chamando createBacklogItem...');
      const result = await createBacklogItem(backlogData);
      console.log('Backlog criado com sucesso:', result);

      // Only update feedback status if backlog was created successfully
      console.log('Atualizando feedback para approved...');
      updateFeedback({
        id: feedback.id,
        approval_status: 'approved',
      });

      // Refetch linked backlog
      refetchLinkedBacklog();
      
      toast.success(t('productFeedback.detail.approvedAndAdded'));
    } catch (error) {
      console.error('=== ERRO ao criar item no backlog ===');
      console.error('Error object:', error);
      console.error('Error message:', error?.message);
      
      // DO NOT approve feedback if backlog creation fails
      // Check for permission/RLS related errors
      const errorMessage = error?.message?.toLowerCase() || '';
      if (errorMessage.includes('permission') || errorMessage.includes('policy') || errorMessage.includes('denied') || errorMessage.includes('rls')) {
        toast.error(t('productFeedback.detail.noPermission'));
      } else {
        toast.error(t('productFeedback.detail.errorCreatingBacklog'));
      }
    }
  };

  const handleReject = () => {
    updateFeedback({
      id: feedback.id,
      approval_status: 'rejected',
      status: 'closed', // Auto-fechar ao rejeitar
    });
  };

  const handleDelete = () => {
    deleteFeedback(feedback.id);
    setShowDeleteDialog(false);
    onClose();
  };

  const handleDownloadScreenshot = async () => {
    const ext = (feedback.screenshot_path?.split('.').pop() || 'png').toLowerCase();
    const fileName = `feedback-${feedback.id.slice(0, 8)}-screenshot.${ext}`;

    try {
      const { data, error } = await supabase
        .storage
        .from('bug-screenshots')
        .download(feedback.screenshot_path);

      if (error) throw error;
      const blob = data instanceof Blob ? data : new Blob([data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('productFeedback.detail.downloadSuccess'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('productFeedback.detail.downloadError'));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-primary" />
              {t('productFeedback.detail.title')}
            </DialogTitle>
            <DialogDescription>
              {t('productFeedback.detail.id')}: {feedback.id}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Header Section with Badges */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={TYPE_CONFIG[feedback.type].className}>
                {TYPE_CONFIG[feedback.type].icon} {t(`productFeedback.type.${feedback.type}`)}
              </Badge>
              <Badge variant="outline" className={STATUS_CONFIG[feedback.status].className}>
                {t(`productFeedback.status.${feedback.status}`)}
              </Badge>
              <Badge variant="outline" className={PRIORITY_CONFIG[feedback.priority].className}>
                {PRIORITY_CONFIG[feedback.priority].label.split(' ')[0]} {t(`productFeedback.priority.${feedback.priority}`)}
              </Badge>
              <Badge variant="outline" className="bg-slate-500/10 text-slate-600 border-slate-500/20">
                {feedback.module}
              </Badge>
              {feedback.approval_status && (
                <Badge variant="outline" className={APPROVAL_CONFIG[feedback.approval_status]?.className}>
                  {t(`productFeedback.approvalStatus.${feedback.approval_status}`)}
                </Badge>
              )}
            </div>

            {/* Approval Actions for Features/Improvements (Admin Only) */}
            {isAdmin && (feedback.type === 'feature' || feedback.type === 'improvement') && feedback.approval_status === 'pending_approval' && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-amber-600">{t('productFeedback.detail.pendingApprovalTitle')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('productFeedback.detail.pendingApprovalDescription', { type: t(`productFeedback.type.${feedback.type}`) })}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="border-green-500 text-green-600 hover:bg-green-500/10"
                      onClick={handleApprove}
                      disabled={isUpdating || isCreatingBacklog}
                    >
                      {isCreatingBacklog ? `⏳ ${t('productFeedback.detail.creating')}` : `✅ ${t('productFeedback.detail.approve')}`}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="border-red-500 text-red-600 hover:bg-red-500/10"
                      onClick={handleReject}
                      disabled={isUpdating}
                    >
                      ❌ {t('productFeedback.detail.reject')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Linked Backlog Item Indicator */}
            {linkedBacklog && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ClipboardList className="h-5 w-5 text-primary" />
                    <div>
                      <h4 className="font-semibold text-primary">{t('productFeedback.detail.linkedBacklog')}</h4>
                      <p className="text-sm text-muted-foreground">{linkedBacklog.title}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={BACKLOG_STATUS_CONFIG[linkedBacklog.status]?.className}>
                      {BACKLOG_STATUS_CONFIG[linkedBacklog.status]?.label || linkedBacklog.status}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      onClick={() => window.open('/it-backlog', '_blank')}
                    >
                      <ExternalLink className="h-3 w-3" />
                      {t('productFeedback.detail.view')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Resposta da equipe — visível para TODOS os usuários quando admin_notes existir */}
            {feedback.admin_notes && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2 text-green-700 font-semibold">
                  <CheckCircle2 className="w-5 h-5" />
                  {t('productFeedback.detail.resolutionResponse')}
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  {t('productFeedback.detail.resolutionDescription')}
                </p>
                <p className="text-sm text-foreground whitespace-pre-wrap">{feedback.admin_notes}</p>
              </div>
            )}

            <Separator />

            {/* Title & Description */}
            <div>
              <h3 className="text-lg font-semibold mb-2">{feedback.title}</h3>
              <p className="text-muted-foreground whitespace-pre-wrap">{feedback.description}</p>
            </div>

            {/* Screenshot */}
            {feedback.screenshot_path && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" />
                    {t('productFeedback.detail.screenshot')}
                  </Label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDownloadScreenshot}
                    className="gap-2"
                  >
                    <Download className="h-4 w-4" />
                    {t('productFeedback.detail.download')}
                  </Button>
                </div>
                
                {imageError ? (
                  <div className="border border-border rounded-lg p-8 flex items-center justify-center bg-muted/20">
                    <p className="text-muted-foreground">{t('productFeedback.detail.screenshotError')}</p>
                  </div>
                ) : loadingScreenshot ? (
                  <div className="border border-border rounded-lg p-8 flex items-center justify-center bg-muted/20">
                    <p className="text-muted-foreground">{t('productFeedback.detail.loadingScreenshot')}</p>
                  </div>
                ) : screenshotUrl ? (
                  <>
                    <div className="border border-border rounded-lg overflow-hidden">
                      <img
                        src={screenshotUrl}
                        alt="Feedback screenshot"
                        className={`w-full ${screenshotExpanded ? 'max-h-none' : 'max-h-96'} object-contain cursor-pointer`}
                        onClick={() => setScreenshotExpanded(!screenshotExpanded)}
                        onError={() => setImageError(true)}
                      />
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setScreenshotExpanded(!screenshotExpanded)}
                      >
                        {screenshotExpanded ? t('productFeedback.detail.collapse') : t('productFeedback.detail.expand')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="border border-border rounded-lg p-8 flex items-center justify-center bg-muted/20">
                    <p className="text-muted-foreground">{t('productFeedback.detail.previewUnavailable')}</p>
                  </div>
                )}
              </div>
            )}

            <Separator />

            {/* Reporter Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <User className="h-4 w-4" />
                  {t('productFeedback.detail.reportedBy')}
                </Label>
                <p className="text-sm">
                  {feedback.reported_by?.display_name || 
                   `${feedback.reported_by?.first_name} ${feedback.reported_by?.last_name}` ||
                   t('productFeedback.detail.unknown')}
                </p>
                {feedback.reported_by?.email && (
                  <p className="text-xs text-muted-foreground">{feedback.reported_by.email}</p>
                )}
              </div>

              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Calendar className="h-4 w-4" />
                  {t('productFeedback.detail.created')}
                </Label>
                <p className="text-sm">{formatDateTime(feedback.created_at)}</p>
              </div>
            </div>

            {feedback.resolved_at && (
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Calendar className="h-4 w-4 text-green-600" />
                  {t('productFeedback.detail.resolvedAt')}
                </Label>
                <p className="text-sm text-green-600">{formatDateTime(feedback.resolved_at)}</p>
              </div>
            )}

            {/* Admin Section */}
            {isAdmin && (
              <>
                <Separator />
                <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">{t('productFeedback.detail.adminSection')}</h4>
                    {!editMode && (
                      <Button size="sm" onClick={() => setEditMode(true)}>
                        {t('productFeedback.detail.edit')}
                      </Button>
                    )}
                  </div>

                  {editMode ? (
                    <div className="space-y-4">
                      {/* Type */}
                      <div>
                        <Label>{t('productFeedback.detail.type')}</Label>
                        <Select
                          value={formData.type}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bug">🐛 {t('productFeedback.type.bug')}</SelectItem>
                            <SelectItem value="feature">✨ {t('productFeedback.type.feature')}</SelectItem>
                            <SelectItem value="improvement">📈 {t('productFeedback.type.improvement')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Status */}
                      <div>
                        <Label>{t('productFeedback.detail.status')}</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">{t('productFeedback.status.open')}</SelectItem>
                            <SelectItem value="in_progress">{t('productFeedback.status.in_progress')}</SelectItem>
                            <SelectItem value="resolved">{t('productFeedback.status.resolved')}</SelectItem>
                            <SelectItem value="closed">{t('productFeedback.status.closed')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Priority */}
                      <div>
                        <Label>{t('productFeedback.detail.priority')}</Label>
                        <Select
                          value={formData.priority}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">🟢 {t('productFeedback.priority.low')}</SelectItem>
                            <SelectItem value="medium">🟡 {t('productFeedback.priority.medium')}</SelectItem>
                            <SelectItem value="high">🟠 {t('productFeedback.priority.high')}</SelectItem>
                            <SelectItem value="critical">🔴 {t('productFeedback.priority.critical')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Assigned To */}
                      <div>
                        <Label>{t('productFeedback.detail.assignedTo')}</Label>
                        <Select
                          value={formData.assigned_to_user_id || 'unassigned'}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, assigned_to_user_id: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">{t('productFeedback.detail.unassigned')}</SelectItem>
                            {users?.map((user) => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.display_name || `${user.first_name} ${user.last_name}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Admin Notes */}
                      <div>
                        <Label>{t('productFeedback.detail.adminNotes')}</Label>
                        <Textarea
                          value={formData.admin_notes}
                          onChange={(e) => setFormData(prev => ({ ...prev, admin_notes: e.target.value }))}
                          placeholder={t('productFeedback.detail.adminNotesPlaceholder')}
                          rows={4}
                        />
                      </div>

                      <div className="flex gap-2">
                        <Button onClick={handleSave} disabled={isUpdating}>
                          {isUpdating ? t('productFeedback.detail.saving') : t('productFeedback.detail.saveChanges')}
                        </Button>
                        <Button variant="outline" onClick={() => setEditMode(false)}>
                          {t('productFeedback.detail.cancel')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {feedback.assigned_to_user_id && (
                        <div>
                          <Label className="text-xs">{t('productFeedback.detail.assignedTo')}</Label>
                          <p className="text-sm">
                            {feedback.assigned_to?.display_name || 
                             `${feedback.assigned_to?.first_name} ${feedback.assigned_to?.last_name}` ||
                             t('productFeedback.detail.unknown')}
                          </p>
                        </div>
                      )}
                      {feedback.admin_notes && (
                        <div>
                          <Label className="text-xs">{t('productFeedback.detail.adminNotes')}</Label>
                          <p className="text-sm whitespace-pre-wrap">{feedback.admin_notes}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <Separator />

                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowDeleteDialog(true)}
                    className="w-full"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('productFeedback.detail.deleteFeedback')}
                  </Button>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              {t('productFeedback.detail.close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('productFeedback.detail.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('productFeedback.detail.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('productFeedback.detail.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? t('productFeedback.detail.deleting') : t('productFeedback.detail.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
